import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ExternalLink,
  Flag,
  Globe,
  Languages,
  Mail,
  MapPin,
  Share2,
  Wallet,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ClinicalDirectorNotice } from "@/components/therapists/ClinicalDirectorNotice";

import { THERAPISTS, type Therapist } from "@/lib/therapists-data";
import {
  listPublishedTherapists,
  mapPublishedRowToTherapist,
} from "@/lib/therapists-directory.functions";
import { isFavorite, toggleFavorite } from "@/lib/favorites";

const FAV_KEY = "rt:fav-therapists";

export const Route = createFileRoute("/therapists_/$therapistId")({
  loader: async ({ params }): Promise<{ therapist: Therapist }> => {
    const staticMatch = THERAPISTS.find((item) => item.id === params.therapistId);
    if (staticMatch) return { therapist: staticMatch };
    try {
      const rows = await listPublishedTherapists();
      const row = (rows ?? []).find((r) => r.id === params.therapistId);
      if (row) return { therapist: mapPublishedRowToTherapist(row) };
    } catch {
      // fall through to notFound
    }
    throw notFound();
  },
  head: ({ loaderData, params }) => {
    const therapist = loaderData?.therapist;
    const title = therapist
      ? `${therapist.name} — Recovery Trauma™ Therapist Directory`
      : "Therapist — Recovery Trauma™";
    const description = therapist
      ? `${therapist.title}. ${therapist.bio}`.slice(0, 160)
      : "Trauma-informed therapist profile.";
    const url = `https://recoverytrauma.com/therapists/${params.therapistId}`;
    const image = therapist?.photo;

    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }

    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: therapist
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                name: therapist.name,
                jobTitle: therapist.title,
                description: therapist.bio,
                image: image ?? undefined,
                url,
                worksFor: {
                  "@type": "Organization",
                  name: "Recovery Trauma™",
                  url: "https://recoverytrauma.com",
                },
              }),
            },
          ]
        : undefined,
    };
  },
  notFoundComponent: TherapistNotFound,
  errorComponent: ({ reset }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <p className="text-foreground">Something went wrong loading this profile.</p>
      <button
        type="button"
        onClick={reset}
        className="mt-4 rounded-full border border-border bg-background px-4 py-2 text-sm"
      >
        Try again
      </button>
    </div>
  ),
  component: TherapistProfilePage,
});

function initialsFor(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.replace(/[^A-Za-z]/g, "").charAt(0))
    .join("")
    .toUpperCase();
}

function DetailSection({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[1.5rem] border border-border/70 bg-card p-6 shadow-[var(--shadow-soft)]">
      {eyebrow && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
      )}
      <h2 className="mt-1 text-xl text-foreground">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function TherapistNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="text-3xl text-foreground">Therapist not found</h1>
      <p className="mt-3 text-muted-foreground">
        This profile may have been removed or the link is incorrect.
      </p>
      <Button asChild variant="goldSoft" className="mt-6 rounded-full px-5">
        <Link to="/therapists">
          <ArrowLeft className="h-4 w-4" /> Back to directory
        </Link>
      </Button>
    </div>
  );
}

function TherapistProfilePage() {
  const { therapist } = Route.useLoaderData();
  const [saved, setSaved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    setSaved(isFavorite(FAV_KEY, therapist.id));
    if (typeof window !== "undefined" && window.location.hash === "#report") {
      setReportOpen(true);
    }
  }, [therapist.id]);

  function handleSave() {
    const { added } = toggleFavorite(FAV_KEY, {
      id: therapist.id,
      label: therapist.name,
      sub: therapist.title,
      to: `/therapists/${therapist.id}`,
    });
    setSaved(added);
    toast.success(added ? "Saved to your favourites" : "Removed from favourites");
  }

  async function handleShare() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const shareData = { title: `${therapist.name} — Recovery Trauma™`, text: therapist.bio, url };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Profile link copied");
      }
    } catch {
      // user dismissed share sheet
    }
  }

  const related = useMemo(
    () =>
      THERAPISTS.filter(
        (candidate) =>
          candidate.id !== therapist.id &&
          (candidate.modalities.some((item) => therapist.modalities.includes(item)) ||
            candidate.specialties.some((item) => therapist.specialties.includes(item)) ||
            candidate.issues.some((item) => therapist.issues.includes(item))),
      ).slice(0, 3),
    [therapist.id, therapist.issues, therapist.modalities, therapist.specialties],
  );

  const primaryFormats = therapist.formats.filter(
    (format: Therapist["formats"][number]) => format === "Online" || format === "In-person",
  );
  const bookingLabel = therapist.profileUrl ? "Book on Therapist Website" : "Visit Booking Page";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
      <Link
        to="/therapists"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to directory
      </Link>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)] lg:items-start">
        <section className="rounded-[2rem] border border-border/70 bg-card p-6 shadow-[var(--shadow-soft-lg)] sm:p-8">
          <div className="grid gap-6 md:grid-cols-[192px_minmax(0,1fr)] md:items-start">
            <Avatar className="h-40 w-40 border border-gold/20 shadow-[var(--shadow-soft)] sm:h-48 sm:w-48">
              <AvatarImage
                src={therapist.photo}
                alt={`Illustrated portrait of ${therapist.name}`}
                loading="lazy"
                width={192}
                height={192}
                className="object-cover"
              />
              <AvatarFallback className="bg-[var(--gold-light)] text-xl font-semibold text-[var(--gold-deep)]">
                {initialsFor(therapist.name)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {therapist.credentials}
                </p>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-3xl leading-tight text-foreground sm:text-4xl">
                      {therapist.name}
                    </h1>
                    
                  </div>
                  {therapist.pronouns && <p className="text-sm text-muted-foreground">{therapist.pronouns}</p>}
                  <p className="text-lg leading-8 text-[var(--gold-deep)]">{therapist.title}</p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-foreground/82 sm:grid-cols-2">
                <div className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 text-[var(--gold-deep)]" />
                  <span>{therapist.location}</span>
                </div>
                <div className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                  <Languages className="mt-0.5 h-4 w-4 text-[var(--gold-deep)]" />
                  <span>{therapist.languages.join(", ")}</span>
                </div>
                <div className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                  <Wallet className="mt-0.5 h-4 w-4 text-[var(--gold-deep)]" />
                  <span>{therapist.feeRange}</span>
                </div>
                <div className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                  <Globe className="mt-0.5 h-4 w-4 text-[var(--gold-deep)]" />
                  <span>{therapist.country}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {primaryFormats.map((format: Therapist["formats"][number]) => (
                  <span
                    key={format}
                    className="rounded-full border border-gold/30 bg-[var(--gold-light)]/55 px-3 py-1 text-xs font-medium text-[var(--gold-deep)]"
                  >
                    {format}
                  </span>
                ))}
                {therapist.acceptingClients ? (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    Accepting clients
                  </span>
                ) : (
                  <span className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
                    Not accepting new clients
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <aside className="lg:sticky lg:top-6">
          <div className="rounded-[1.75rem] border border-gold/20 bg-[linear-gradient(180deg,color-mix(in_oklab,var(--gold-light)_50%,white)_0%,color-mix(in_oklab,var(--card)_95%,var(--gold-light)_5%)_100%)] p-6 shadow-[var(--shadow-soft-lg)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              Main call to action
            </p>
            <div className="mt-4 space-y-3">
              <Button asChild variant="goldSoft" className="h-12 w-full rounded-full text-sm">
                <a href={therapist.profileUrl} target="_blank" rel="noopener noreferrer">
                  {bookingLabel}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>

              {therapist.website && (
                <Button asChild variant="outline" className="h-11 w-full rounded-full">
                  <a href={therapist.website} target="_blank" rel="noopener noreferrer">
                    Website
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}

              {therapist.contactEmail && (
                <Button asChild variant="outline" className="h-11 w-full rounded-full">
                  <a href={`mailto:${therapist.contactEmail}`}>
                    <Mail className="h-4 w-4" /> Contact
                  </a>
                </Button>
              )}
            </div>

            <Separator className="my-5 bg-border/70" />

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full"
                onClick={handleSave}
                aria-pressed={saved}
              >
                {saved ? "Saved" : "Save therapist"}
              </Button>
              <Button type="button" variant="ghost" className="rounded-full" onClick={handleShare}>
                <Share2 className="h-4 w-4" /> Share
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-muted-foreground"
                onClick={() => setReportOpen(true)}
              >
                <Flag className="h-4 w-4" /> Report
              </Button>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)]">
        <div className="space-y-6">
          <DetailSection eyebrow="Biography" title="About this therapist">
            <p className="whitespace-pre-line text-sm leading-8 text-foreground/82">{therapist.bio}</p>
          </DetailSection>

          {therapist.qualifications && therapist.qualifications.length > 0 && (
            <DetailSection eyebrow="Credentials" title="Qualifications">
              <ul className="space-y-3 text-sm leading-7 text-foreground/82">
                {therapist.qualifications.map((item: string) => (
                  <li key={item} className="rounded-2xl border border-border/60 bg-background px-4 py-3">
                    {item}
                  </li>
                ))}
              </ul>
            </DetailSection>
          )}

          <DetailSection eyebrow="Experience" title="Years of experience">
            <p className="text-sm leading-8 text-foreground/82">
              <span className="text-3xl text-foreground">{therapist.yearsExperience}</span> years supporting clients through trauma-informed work.
            </p>
          </DetailSection>

          <DetailSection eyebrow="Approach" title="Therapy approaches">
            <div className="flex flex-wrap gap-2">
              {therapist.modalities.map((item: string) => (
                <span key={item} className="rounded-full border border-gold/25 bg-[var(--gold-light)]/45 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)]">
                  {item}
                </span>
              ))}
            </div>
          </DetailSection>

          <DetailSection eyebrow="Focus areas" title="Specialisms">
            <div className="flex flex-wrap gap-2">
              {[...therapist.specialties, ...therapist.issues].map((item, index) => (
                <span key={`${item}-${index}`} className="rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs text-foreground/80">
                  {item}
                </span>
              ))}
            </div>
          </DetailSection>

          <ClinicalDirectorNotice />
        </div>

        <div className="space-y-6">
          <DetailSection eyebrow="Languages" title="Languages spoken">
            <div className="flex flex-wrap gap-2">
              {therapist.languages.map((item: string) => (
                <span key={item} className="rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs text-foreground/80">
                  {item}
                </span>
              ))}
            </div>
          </DetailSection>

          <DetailSection eyebrow="Licensing" title="Countries or regions licensed to practise in">
            <div className="space-y-4 text-sm leading-7 text-foreground/82">
              {therapist.licensingJurisdiction && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Jurisdiction
                  </p>
                  <p className="mt-1">{therapist.licensingJurisdiction}</p>
                </div>
              )}
              {therapist.licensedStates && therapist.licensedStates.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Licensed states
                  </p>
                  <p className="mt-1">{therapist.licensedStates.join(", ")}</p>
                </div>
              )}
              {therapist.licensingNote && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Licensing note
                  </p>
                  <p className="mt-1">{therapist.licensingNote}</p>
                </div>
              )}
              {therapist.internationalAvailability && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    International availability
                  </p>
                  <p className="mt-1">{therapist.internationalAvailability}</p>
                </div>
              )}
              {!therapist.licensingJurisdiction && !therapist.licensingNote && !therapist.internationalAvailability && (
                <p className="text-muted-foreground">Please contact the therapist directly to confirm location-specific availability before booking.</p>
              )}
            </div>
          </DetailSection>

          <DetailSection eyebrow="Session details" title="Availability">
            <div className="space-y-4 text-sm leading-7 text-foreground/82">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Online / In-person availability
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {therapist.formats.map((item: Therapist["formats"][number]) => (
                    <span key={item} className="rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs text-foreground/80">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Current availability
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {therapist.availability.map((item: Therapist["availability"][number]) => (
                    <span key={item} className="rounded-full border border-gold/25 bg-[var(--gold-light)]/45 px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)]">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              {therapist.insuranceInfo && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Insurance
                  </p>
                  <p className="mt-1">{therapist.insuranceInfo}</p>
                </div>
              )}
            </div>
          </DetailSection>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Similar profiles
              </p>
              <h2 className="mt-1 text-2xl text-foreground">You may also want to explore</h2>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to="/therapists/$therapistId"
                params={{ therapistId: item.id }}
                className="rounded-[1.5rem] border border-border/70 bg-card p-5 shadow-[var(--shadow-soft)] transition-transform duration-200 hover:-translate-y-0.5 hover:border-gold/30 hover:shadow-[var(--shadow-soft-lg)]"
              >
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 border border-gold/20">
                    <AvatarImage src={item.photo} alt="" loading="lazy" width={64} height={64} className="object-cover" />
                    <AvatarFallback className="bg-[var(--gold-light)] text-xs font-semibold text-[var(--gold-deep)]">
                      {initialsFor(item.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h3 className="truncate text-lg text-foreground">{item.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.title}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 rounded-[1.5rem] border border-border/70 bg-card p-5 text-xs leading-7 text-muted-foreground shadow-[var(--shadow-soft)]">
        <p>
          <strong className="text-foreground">Listing disclaimer.</strong> This profile is provided for informational purposes only and does not constitute a clinical recommendation, referral, or endorsement. Viewing this profile does not establish a provider–patient relationship.
        </p>
        <p className="mt-3">
          <strong className="text-foreground">Recovery Trauma™'s role.</strong> Recovery Trauma Ltd operates a directory listing service. We do not employ, supervise, clinically assess, recommend, endorse or guarantee therapists or their services, and we are not a party to the therapist–client relationship. Appointments, treatment, contracts and payments take place directly between the practitioner and the client. Each practitioner remains independently responsible for their qualifications, licensing, insurance, and legal compliance.
        </p>
        <p className="mt-3">
          <strong className="text-foreground">Document verification.</strong> Where a profile shows a Documents Verified badge, we reviewed specified professional documents before listing. This is an administrative check, not a clinical assessment. It does not guarantee a practitioner's competence, suitability, conduct, treatment or outcomes. Please check credentials and registration independently before engaging a practitioner.
        </p>

      </div>

      {reportOpen && (
        <ReportConcernDialog therapistName={therapist.name} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}

function ReportConcernDialog({
  therapistName,
  onClose,
}: {
  therapistName: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("Incorrect qualifications");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    "Incorrect qualifications",
    "Incorrect registration details",
    "Expired licensing",
    "Unprofessional conduct",
    "Broken website links",
    "Safety concerns",
    "Inaccurate profile information",
    "Other",
  ];

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (description.trim().length < 10) {
      toast.error("Please add a short description (at least 10 characters).");
      return;
    }

    setSubmitting(true);
    const subject = `Therapist concern — ${therapistName} (${reason})`;
    const bodyLines = [
      `Therapist: ${therapistName}`,
      `Reason: ${reason}`,
      "",
      "Description:",
      description.trim(),
      "",
      `Reporter contact: ${contactEmail.trim() || "(not provided)"}`,
      "",
      "— Submitted via Recovery Trauma™ Therapist Directory",
    ];
    const href = `mailto:support@recoverytrauma.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    if (typeof window !== "undefined") window.location.href = href;
    toast.success("Opening your email to send the report…");
    setTimeout(() => {
      setSubmitting(false);
      onClose();
    }, 400);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-concern-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="report-concern-title" className="text-xl text-foreground">
              Report a concern
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              About <strong className="text-foreground">{therapistName}</strong>. Reports go to{" "}
              <a className="underline" href="mailto:support@recoverytrauma.com">
                support@recoverytrauma.com
              </a>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Reason for concern
            </label>
            <select
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--gold-deep)]"
            >
              {reasons.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value.slice(0, 2000))}
              rows={5}
              required
              minLength={10}
              maxLength={2000}
              placeholder="Please describe the issue in your own words."
              className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--gold-deep)]"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{description.length}/2000</p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Your contact email <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value.slice(0, 255))}
              maxLength={255}
              placeholder="you@example.com"
              className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--gold-deep)]"
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold-soft inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm disabled:opacity-60"
            >
              <Flag className="h-4 w-4" /> Submit report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
